import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '@core/environments/environment';
import { markClarityPageChange } from '@core/utils/clarity-console-error-filter';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService } from './auth.service';

declare const clarity: (action: string, ...args: unknown[]) => void;

@Injectable({
  providedIn: 'root',
})
export class ClarityService implements OnDestroy {
  private clarityLoaded = false;
  private userSubscription?: Subscription;

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
  ) {
    this.initializeClarity();
    this.trackPageViews();
    this.setupUserTracking();
  }

  public ngOnDestroy(): void {
    this.userSubscription?.unsubscribe();
  }

  /**
   * Setup automatic user identification when user logs in
   */
  private setupUserTracking(): void {
    // Listen to user changes and identify when user is authenticated
    this.userSubscription = this.authService.currentDbUser$.subscribe((user) => {
      if (user) {
        this.identifyUser(user.id, {
          email: AuthService.currentFirebaseUser?.email,
          name: user.name,
          userGroupId: user.userGroupId,
        });
      }
    });
  }

  /**
   * Initialize Microsoft Clarity if configured
   */
  private initializeClarity(): void {
    if (environment.clarity?.enabled !== false && environment.clarity?.projectId) {
      this.loadClarityScript(environment.clarity.projectId);
    }
  }

  /**
   * Load Microsoft Clarity script dynamically
   * Clarity automatically tracks all user interactions once loaded:
   * - Clicks, scrolls, mouse movements
   * - Rage clicks (automatic detection)
   * - Dead clicks (automatic detection)
   * - Session recordings
   * - Heatmaps
   */
  private loadClarityScript(projectId: string): void {
    if (this.clarityLoaded) {
      return;
    }

    // Check if Clarity is already loaded (e.g., from index.html)
    if (typeof window !== 'undefined' && (window as { clarity?: unknown }).clarity) {
      this.clarityLoaded = true;

      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.innerHTML = `
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "${projectId}");
    `;

    script.onload = (): void => {
      this.clarityLoaded = true;
    };

    script.onerror = (): void => {
      // eslint-disable-next-line no-console
      console.warn('Failed to load Microsoft Clarity script');
    };

    document.head.appendChild(script);
    this.clarityLoaded = true; // Set immediately as script loads async
  }

  /**
   * Track page views automatically on route changes
   */
  private trackPageViews(): void {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.trackPageView(event.urlAfterRedirects);
    });
  }

  /**
   * Track a page view
   */
  public trackPageView(url: string): void {
    if (this.isClarityReady()) {
      markClarityPageChange();
      clarity('set', 'page', url);
    }
  }

  /**
   * Check if Clarity is ready to use
   */
  private isClarityReady(): boolean {
    return typeof window !== 'undefined' && typeof (window as { clarity?: unknown }).clarity === 'function';
  }

  /**
   * Track a custom event
   */
  public trackEvent(eventName: string, eventData?: Record<string, unknown>): void {
    if (this.isClarityReady()) {
      clarity('event', eventName, eventData);
    }
  }

  /**
   * Identify a user (call this after login)
   */
  public identifyUser(userId: string, userProperties?: Record<string, unknown>): void {
    if (this.isClarityReady()) {
      clarity('identify', userId, userProperties || {});
    }
  }

  /**
   * Track user actions (clicks, form submissions, etc.)
   */
  public trackAction(actionName: string, metadata?: Record<string, unknown>): void {
    this.trackEvent(`action_${actionName}`, metadata);
  }

  /**
   * Track time spent on a page or section
   */
  public trackTimeSpent(section: string, duration: number): void {
    this.trackEvent('time_spent', {
      duration,
      section,
    });
  }

  /**
   * Track rage clicks (multiple rapid clicks on same element)
   */
  public trackRageClick(element: string, clickCount: number): void {
    this.trackEvent('rage_click', {
      clickCount,
      element,
    });
  }
}
